package ai.wagemix.net.models.firebase

import ai.wagemix.net.models.KxsWMStatsCollection
import ai.wagemix.net.models.WMBadge
import ai.wagemix.net.models.WMSharedMixPurpose
import ai.wagemix.net.models.WMStatsCollection
import com.fsryan.foundation.models.toEnumValue
import kotlinx.serialization.Serializable
import kotlinx.serialization.SerialName

interface WMSharedMix {
    val badge: WMBadge
    val data: WMStatsCollection
    val filters: Filters

    val mode: String // "hourly" or "annual"
    val occupations: List<WMFirebaseMix.Occupation>
    val payRange: PayRange?
    val previewImageUrl: String?
    val purpose: WMSharedMixPurpose
    val statFilters: StatFilters
    val title: String
    /** "First L." of the sharer; absent on documents created before the site rebuild (task API-11). */
    val sharedBy: String?
    /** ISO-8601 instant of the share; absent on older documents. */
    val sharedAt: String?


    /**
     * Static, pre-computed chart data for rendering
     */
    interface StaticChartData {
        val stats: Map<String, WMStatsCollection.OccupationStats>
        val area: String?
        val industry: String?
        val regressionMetadata: WMStatsCollection.RegressionMetadata?

        companion object
    }

    interface Filters {
        val area: String?
        val industry: String?

        companion object
    }

    interface StatFilters {
        val p10: Boolean
        val p25: Boolean
        val median: Boolean
        val p75: Boolean
        val p90: Boolean
        companion object
    }

    interface PayRange {
        val low: Int
        val high: Int
        companion object
    }

    companion object
}

@Serializable
@ConsistentCopyVisibility
data class KxsWMSharedMix internal constructor(
    @SerialName("badge") private val _badge: String = WMBadge.None.stringValue,
    override val data: KxsWMStatsCollection,
    override val filters: Filters = Filters(),
    override val mode: String = "annual",
    override val occupations: List<KxsWMFirebaseMix.Occupation> = emptyList(),
    @SerialName("payrange") override val payRange: PayRange? = null,
    override val previewImageUrl: String? = null,
    @SerialName("purpose") private val _purpose: String = WMSharedMixPurpose.Social.stringValue,
    @SerialName("stat_filters") override val statFilters: StatFilters = StatFilters(),
    override val title: String = "",
    @SerialName("shared_by") override val sharedBy: String? = null,
    @SerialName("shared_at") override val sharedAt: String? = null
): WMSharedMix {
    override val badge: WMBadge
        get() = _badge.toEnumValue(WMBadge.None)
    override val purpose: WMSharedMixPurpose
        get() = _purpose.toEnumValue(WMSharedMixPurpose.Unknown)

    @Serializable
    @ConsistentCopyVisibility
    data class Filters internal constructor(
        @SerialName("area") override val area: String? = null,
        @SerialName("industry") override val industry: String? = null
    ): WMSharedMix.Filters

    @Serializable
    @ConsistentCopyVisibility
    data class StatFilters internal constructor(
        override val p10: Boolean = true,
        override val p25: Boolean = true,
        override val median: Boolean = true,
        override val p75: Boolean = true,
        override val p90: Boolean = true
    ): WMSharedMix.StatFilters

    @Serializable
    @ConsistentCopyVisibility
    data class PayRange internal constructor(
        override val low: Int,
        override val high: Int
    ): WMSharedMix.PayRange
}
